import type { Edge, Node } from '@xyflow/react';
import type { NodeTemplate } from '@features/flows/palette/registry';
import { synthesizeSchemaFromTypeExpr } from './schema-utils';

export type SchemaSource = 'template_pin' | 'pin' | 'pin_type_expr' | 'template' | 'fallback';

export type EdgeSchemaContext = {
  rawSource: string;
  rawTarget: string;
  sourceNode?: Node;
  targetNode?: Node;
  sourceTemplate?: NodeTemplate;
  targetTemplate?: NodeTemplate;
  sourceSchema: unknown;
  targetSchema: unknown;
  sourceSchemaSource: SchemaSource;
  targetSchemaSource: SchemaSource;
  targetInputPath?: string | null;
  targetInputSchema?: unknown;
};

export function parseHandleToken(handle: string | null | undefined, fallback: string): string {
  if (!handle) return fallback;
  const parts = String(handle).split(':');
  if (parts.length >= 3) return parts[2]!;
  const tail = parts[parts.length - 1];
  return tail && tail.length ? tail : fallback;
}

export function getTemplateForNode(
  node: Node | undefined,
  templates: Map<string, NodeTemplate> | null | undefined,
): NodeTemplate | undefined {
  if (!node || !templates) return undefined;
  const key = (node.data as any)?.templateKey || (node.data as any)?.handler;
  if (!key) return undefined;
  return templates.get(String(key));
}

const schemaFromTemplate = (tpl: NodeTemplate | undefined, io: 'in' | 'out', handle: string) => {
  if (!tpl) return undefined;
  const schema = io === 'out' ? (tpl as any)?.outputSchema : (tpl as any)?.inputSchema;
  if (!schema || typeof schema !== 'object') return undefined;
  const props = (schema as any).properties;
  if (props && typeof props === 'object' && handle && (props as any)[handle]) {
    return (props as any)[handle];
  }
  return undefined;
};

type NodeCollection = Node[] | Record<string, Node>;

const findNode = (nodes: NodeCollection, id: string | null | undefined): Node | undefined => {
  if (!id) return undefined;
  if (Array.isArray(nodes)) {
    return nodes.find((n) => n.id === id);
  }
  return nodes[id] ?? undefined;
};

export function resolveEdgeSchemas(
  edge: Edge,
  nodes: NodeCollection,
  templates: Map<string, NodeTemplate> | null | undefined,
): EdgeSchemaContext {
  const data = (edge as any).data ?? {};
  const rawSource = data.rawSourceHandle ?? parseHandleToken(edge.sourceHandle as any, 'out');
  const rawTarget = data.rawTargetHandle ?? parseHandleToken(edge.targetHandle as any, 'in');

  const sourceNode = findNode(nodes, edge.source);
  const targetNode = findNode(nodes, edge.target);

  const sourceTemplate = getTemplateForNode(sourceNode, templates);
  const targetTemplate = getTemplateForNode(targetNode, templates);

  const sourcePin = (sourceNode?.data as any)?.pins?.data?.out?.find((p: any) => p.id === rawSource);
  const targetPin = (targetNode?.data as any)?.pins?.data?.in?.find((p: any) => p.id === rawTarget);

  const fullSourceSchema = (sourceTemplate as any)?.outputSchema;
  const fullTargetSchema = (targetTemplate as any)?.inputSchema;

  let sourceSchemaSource: SchemaSource = 'fallback';
  let targetSchemaSource: SchemaSource = 'fallback';
  let targetInputPath: string | null = null;

  let sourceSchema = schemaFromTemplate(sourceTemplate, 'out', rawSource);
  if (sourceSchema) {
    sourceSchemaSource = 'template_pin';
  } else if (sourcePin?.schema) {
    sourceSchema = sourcePin.schema;
    sourceSchemaSource = 'pin';
  } else if (sourcePin?.typeExpr) {
    sourceSchema = synthesizeSchemaFromTypeExpr(sourcePin.typeExpr);
    sourceSchemaSource = 'pin_type_expr';
  } else if (fullSourceSchema) {
    sourceSchema = fullSourceSchema;
    sourceSchemaSource = 'template';
  } else {
    sourceSchema = {};
  }

  let targetSchema = schemaFromTemplate(targetTemplate, 'in', rawTarget);
  if (targetSchema) {
    targetSchemaSource = 'template_pin';
    targetInputPath = rawTarget;
  } else if (targetPin?.schema) {
    targetSchema = targetPin.schema;
    targetSchemaSource = 'pin';
    targetInputPath = rawTarget;
  } else if (targetPin?.typeExpr) {
    targetSchema = synthesizeSchemaFromTypeExpr(targetPin.typeExpr);
    targetSchemaSource = 'pin_type_expr';
    targetInputPath = rawTarget;
  } else if (fullTargetSchema) {
    targetSchema = fullTargetSchema;
    targetSchemaSource = 'template';
  } else {
    targetSchema = {};
  }

  return {
    rawSource,
    rawTarget,
    sourceNode,
    targetNode,
    sourceTemplate,
    targetTemplate,
    sourceSchema,
    targetSchema,
    sourceSchemaSource,
    targetSchemaSource,
    targetInputPath,
    targetInputSchema: fullTargetSchema,
  };
}
